import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaStaticNodeData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import findFirstMatchingNodeAlongEveryPath from 'util/graph-utils/findFirstMatchingNodeAlongEveryPath';

function isMappableNode(node: {id: string; data: TaskSchemaStaticNodeData}): boolean {
  return [
    ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
    ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
    ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
    ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
  ].includes(node.data.nodeDetailsType);
}

function buildAdjacencyMap({
  edges,
  shouldIncludeEdge = (edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW,
  getAdjacencyMapKey = (edge) => edge.source,
  getAdjacencyMapValue = (edge) => edge.target,
}: {
  edges: Array<FlowchartStaticEdgeData>;
  shouldIncludeEdge?: (edge: FlowchartStaticEdgeData) => boolean;
  getAdjacencyMapKey?: (edge: FlowchartStaticEdgeData) => string;
  getAdjacencyMapValue?: (edge: FlowchartStaticEdgeData) => string;
}): Record<string, Array<string>> {
  const adjacencyMap: Record<string, Array<string>> = {};
  for (const edge of edges) {
    if (!shouldIncludeEdge(edge)) {
      continue;
    }
    const key = getAdjacencyMapKey(edge);
    const value = getAdjacencyMapValue(edge);
    if (adjacencyMap[key] == null) {
      adjacencyMap[key] = [];
    }
    adjacencyMap[key].push(value);
  }
  return adjacencyMap;
}

/**
 * Finds all nodes that are downstream of the starting node and can be mapped from the starting node.
 *
 * A downstream node can be mapped from the starting node if:
 * 1. The downstream node is a node which corresponds to a ProcessTaskSchema (meaning it is a root, task or output node)
 * 2. The only nodes between the starting node and the downstream node are router nodes
 *
 * @param flowchart - The flowchart to traverse.
 * @param startingNodeId - The node to start traversing from.
 * @returns An array of node IDs that are downstream of the starting node and are mapped from the starting node.
 */
export function findAllDownstreamMappableNodeIds(
  flowchart: {
    nodes: Array<{id: string; data: TaskSchemaStaticNodeData}>;
    edges: Array<FlowchartStaticEdgeData>;
  },
  startingNodeId: string
): Array<string> {
  const nodes = flowchart.nodes;
  const edges = flowchart.edges;

  const adjacencyMap = buildAdjacencyMap({edges});

  const nodesAndPaths = findFirstMatchingNodeAlongEveryPath(startingNodeId, adjacencyMap, (nodeId) => {
    const node = nodes.find((node) => node.id === nodeId);
    return node != null && isMappableNode(node);
  });

  return nodesAndPaths.map((nodeAndPath) => nodeAndPath.nodeId);
}

/**
 * Finds all nodes that are upstream of the starting node and can map to the starting node.
 *
 * An upstream node can be mapped from the starting node if:
 * 1. The upstream node is a node which corresponds to a ProcessTaskSchema (meaning it is a root, task or output node)
 * 2. The only nodes between the starting node and the upstream node are router nodes
 *
 * @param flowchart - The flowchart to traverse.
 * @param startingNodeId - The node to start traversing from.
 * @returns An array of node IDs that are upstream of the starting node and can map to the starting node.
 */
export function findAllUpstreamMappableNodeIds(
  flowchart: {
    nodes: Array<{id: string; data: TaskSchemaStaticNodeData}>;
    edges: Array<FlowchartStaticEdgeData>;
  },
  startingNodeId: string
): Array<string> {
  const nodes = flowchart.nodes;
  const edges = flowchart.edges;

  const invertedAdjacencyMap = buildAdjacencyMap({
    edges,
    getAdjacencyMapKey: (edge) => edge.target,
    getAdjacencyMapValue: (edge) => edge.source,
  });

  const nodesAndPaths = findFirstMatchingNodeAlongEveryPath(
    startingNodeId,
    invertedAdjacencyMap,
    (nodeId) => {
      const node = nodes.find((node) => node.id === nodeId);
      return node != null && isMappableNode(node);
    }
  );

  return nodesAndPaths.map((nodeAndPath) => nodeAndPath.nodeId);
}
